import {
  DialogContent,
  DialogTitle,
  IconButton,
  Button,
  CircularProgress,
  Autocomplete,
  Typography
} from '@mui/material';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';

import Container from '@mui/material/Container';
import CloseIcon from '@mui/icons-material/Close';

import Grid from '@mui/material/Grid';
import React, { memo, useContext, useEffect, useState } from 'react';
import { debounce, isEmpty } from 'lodash';
import styles from '../HPRA/styles/labelSeparation.styles';
import { RRTableRow, Product } from './types';
import {
  getPMDAReviewReport,
  getProductSuggestions,
  updateReviewReportResource
} from '../../api/pmda';
import Actions from '../../store/actions';
import Store from '../../store';

const ReviewReportDialog = ({
  closeEdit,
  resourceId
}: {
  closeEdit: () => void;
  resourceId: number | undefined;
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [loadingOptions, setLoadingOptions] = useState(false);

  const [reviewResource, setReviewResource] = useState<RRTableRow>();
  const [productSuggestions, setProductSuggestions] = React.useState<readonly Product[]>([]);
  const [selectedProduct, setSelectedProduct] = React.useState<Product>();

  const [approvedDate, setApprovedDate] = React.useState<string>();
  const [showPDF, setShowPDF] = React.useState<boolean>(false);

  const { dispatch } = useContext<any>(Store);

  const debouncedProductSuggestions = async (newValue: string) => {
    try {
      if (newValue) {
        setLoadingOptions(true);
        const response = await getProductSuggestions(newValue);
        if (response.data.status === 200) {
          setProductSuggestions(response.data.body);
        }
        setLoadingOptions(false);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
    }
  };

  const handleProductSuggestionChange = debounce(debouncedProductSuggestions, 300);

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    try {
      setIsSubmitting(true);
      // eslint-disable-next-line no-unused-vars
      const response = await updateReviewReportResource({
        ...reviewResource,
        yj_code: selectedProduct?.yj_code,
        active_ingredient_id: selectedProduct?.active_ingredient_id,
        approved_date: approvedDate
      });

      setIsSubmitting(false);
      if (!response.data.error && response.data.status === 200) closeEdit();
      else
        dispatch({
          type: Actions.SET_ALERT,
          value: { status: true, message: `Error: ${response.data.message}` }
        });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
    }
  };

  useEffect(() => {
    const getData = async (id: number) => {
      try {
        setIsLoading(true);
        const result = await getPMDAReviewReport(id);

        setIsLoading(false);

        if (!result?.data?.body?.result) {
          dispatch({
            type: Actions.SET_ALERT,
            value: { status: true, message: 'Error: Review Report not found' }
          });
        }
        const resource = result.data.body.result;
        setReviewResource(resource);
        setApprovedDate(resource?.approved_date ?? '');
      } catch (error) {
        await dispatch({
          type: Actions.SET_ALERT,
          value: { status: true, message: (error as Error).message }
        });
      }
    };

    if (resourceId) getData(resourceId);
  }, [resourceId]);

  return (
    <div>
      <Container sx={{ overflow: 'hidden' }}>
        <DialogTitle sx={{ m: 0, p: 2 }}>
          <Box display='flex' alignItems='center'>
            Review Report
          </Box>
          <IconButton
            aria-label='close'
            onClick={() => {
              closeEdit();
            }}
            sx={{
              position: 'absolute',
              right: 5,
              top: 2,
              color: 'gray.light'
            }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {isLoading ? (
            <CircularProgress />
          ) : (
            <Grid container spacing={2} display='flex'>
              <Grid
                item
                xs={12}
                lg={showPDF ? 6 : 12}
                mb={showPDF ? 3 : 12}
                sx={{ overflow: 'auto', height: '75vh', padding: 2 }}>
                <Button
                  variant='contained'
                  color='primary'
                  onClick={() => {
                    setShowPDF(show => !show);
                  }}
                  style={{ color: 'white' }}>
                  <Typography>{showPDF ? 'Close PDF' : 'Open PDF'}</Typography>
                </Button>
                <form onSubmit={handleSubmit} style={styles.form}>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <TextField
                        variant='outlined'
                        fullWidth
                        id='resource_id'
                        label='Resource id'
                        value={reviewResource?.resource_id}
                        aria-readonly
                        InputProps={{
                          style: { fontSize: '2vh' }
                        }}
                        InputLabelProps={{
                          style: { fontSize: '2vh' }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        variant='outlined'
                        fullWidth
                        id='name'
                        label='Name'
                        value={reviewResource?.name}
                        aria-readonly
                        InputProps={{
                          style: { fontSize: '2vh' }
                        }}
                        InputLabelProps={{
                          style: { fontSize: '2vh' }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        variant='outlined'
                        fullWidth
                        id='brand_name'
                        label='Brand Name'
                        aria-readonly
                        value={reviewResource?.brand_name}
                        InputProps={{
                          style: { fontSize: '2vh' }
                        }}
                        InputLabelProps={{
                          style: { fontSize: '2vh' }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        variant='outlined'
                        fullWidth
                        id='approved_date'
                        label='Approved Date'
                        value={approvedDate}
                        onChange={event => {
                          setApprovedDate(event.target.value);
                        }}
                        InputProps={{
                          style: { fontSize: '2vh' }
                        }}
                        InputLabelProps={{
                          style: { fontSize: '2vh' }
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Autocomplete
                        getOptionLabel={productSuggestion => productSuggestion.trade_name}
                        options={productSuggestions}
                        loading={loadingOptions}
                        onInputChange={(event, newValue) => {
                          handleProductSuggestionChange(newValue || '');
                        }}
                        onChange={(event, newValue) => {
                          setSelectedProduct(newValue as Product);
                        }}
                        renderInput={(params: any) => (
                          // eslint-disable-next-line react/jsx-props-no-spreading
                          <TextField {...params} fullWidth variant='outlined' label='Product' />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        type='submit'
                        fullWidth
                        variant='contained'
                        color='primary'
                        sx={styles.submit}
                        disabled={
                          Object.keys(selectedProduct ?? {}).length === 0 ||
                          isEmpty(approvedDate) ||
                          isSubmitting
                        }>
                        {isSubmitting ? 'SUBMITTING' : 'SUBMIT'}
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              </Grid>

              {showPDF && (
                <Grid item xs={12} lg={6} sx={{ height: '70vh' }}>
                  <iframe
                    src={reviewResource?.pdf_url ?? ''}
                    title='Product Resource'
                    style={{ height: '100%', width: '100%' }}
                  />
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
      </Container>
    </div>
  );
};

export default memo(ReviewReportDialog);
